// Implements: F-UIX-009, F-UIX-004, F-UIX-007
/**
 * The three controls of the sidebar that act rather than navigate: the organization switcher, the
 * three-state theme control and the user menu (the mockup's `.org-sw`, theme `.nav-item` and
 * `.user-btn`).
 *
 * The switcher lists the organizations `/auth/me/scopes` granted (`activeOrganization`); "Crear
 * organización" is not offered because no permission answers it. The user menu holds what the server
 * serves: the person's name and email, the active sessions and the sign-out. "Perfil y preferencias"
 * is not offered: there is no profile endpoint (feature doc, Backend gaps).
 */

import { useCallback } from "react";
import type { ReactElement } from "react";
import { useNavigate } from "react-router";

import { useMe } from "../../modules/account/hooks/useMe";
import { useLogout } from "../../modules/auth/hooks/useLogout";
import { useActiveOrganization } from "../../shared/api/activeOrganization";
import type { IconName } from "../../shared/design-system/icons";
import { useTranslation } from "../../shared/i18n";
import { useTheme } from "../../shared/theme/ThemeProvider";
import type { Theme } from "../../shared/theme/ThemeProvider";
import { Avatar } from "../../shared/ui/atoms/Avatar";
import { Icon } from "../../shared/ui/atoms/Icon";
import { Menu } from "../../shared/ui/molecules/Menu";
import type { MenuEntry } from "../../shared/ui/molecules/Menu";
import { useToast } from "../../shared/ui/molecules/ToastProvider";
import type { UserSummary } from "../../types/api";
import { ACCOUNT_TABS, accountHref } from "./accountTabs";
import styles from "./Sidebar.module.css";

/** The cycle of F-UIX-004, written as data so the test can walk it. */
export const THEME_CYCLE: Readonly<Record<Theme, Theme>> = {
  system: "light",
  light: "dark",
  dark: "system",
};

const THEME_GLYPH: Readonly<Record<Theme, IconName>> = { system: "monitor", light: "sun", dark: "moon" };
const THEME_LABEL = {
  system: "common.theme.system",
  light: "common.theme.light",
  dark: "common.theme.dark",
} as const;

export function OrgSwitcher(): ReactElement {
  const { t } = useTranslation();
  const { organizations, active, select } = useActiveOrganization();
  const name = active?.name ?? t("app.org.personal");
  // Named by the words it shows, then what it does (WCAG 2.5.3); the flex column does not render the space between them.
  const kind = active === null ? t("app.org.personalHint") : t("app.org.kind");
  const entries: MenuEntry[] =
    organizations.length === 0
      ? [{ kind: "radio", id: "personal", label: t("app.org.personal"), icon: "user", checked: true, onSelect: () => undefined }]
      : organizations.map((organization) => ({
          kind: "radio",
          id: organization.id,
          label: organization.name,
          icon: "building",
          checked: organization.id === active?.id,
          onSelect: () => {
            select(organization.id);
          },
        }));

  return (
    <Menu
      label={t("app.org.label")}
      entries={entries}
      renderTrigger={(trigger) => (
        <button type="button" {...trigger} className={[styles.control, styles.switcher].join(" ")} aria-label={t("app.org.switch", { name, kind })}>
          <Avatar name={name} shape="square" />
          <span className={styles.text}>
            <b>{name}</b> <small>{kind}</small>
          </span>
          <span className={styles.chevron}>
            <Icon name="chevron-up-down" size="sm" />
          </span>
        </button>
      )}
    />
  );
}

export function ThemeControl(): ReactElement {
  const { t } = useTranslation();
  const { theme, setTheme } = useTheme();
  const current = t(THEME_LABEL[theme]);
  return (
    <button
      type="button"
      className={[styles.control, styles.navLike].join(" ")}
      aria-label={t("app.theme.cycle", { theme: current })}
      onClick={() => {
        setTheme(THEME_CYCLE[theme]);
      }}
    >
      <Icon name={THEME_GLYPH[theme]} />
      <span className={styles.text}>
        {t("app.theme.current", { theme: current })}
      </span>
    </button>
  );
}

export interface UserMenuProps {
  readonly user: UserSummary;
  /** Extra actions the shell adds (the shortcuts sheet), before the sign-out. */
  readonly extra?: readonly MenuEntry[] | undefined;
}

/** Signs out; a sign-out the server never confirmed is said out loud (user menu and palette). */
export function useSignOut(): () => void {
  const { t } = useTranslation();
  const { signOut } = useLogout();
  const { push } = useToast();

  return useCallback((): void => {
    void signOut().then((signedOut) => {
      if (signedOut) return;
      // The browser has been emptied and is already on `/login`, so the screen alone would read as a
      // completed sign-out. It was not one: the server never confirmed, and the cookie it holds may
      // still be valid. `danger` is announced as `role="alert"` and does not expire on its own.
      push({ tone: "danger", message: t("auth.logout.failed"), timeoutMs: 0 });
    });
  }, [signOut, push, t]);
}

export function UserMenu({ user, extra = [] }: UserMenuProps): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const handleSignOut = useSignOut();
  // The picture comes from the account (mockup v3 P1); the initials until there is one.
  const me = useMe();

  const entries: MenuEntry[] = [
    { kind: "label", id: "name", label: user.full_name },
    { kind: "label", id: "email", label: user.email },
    { kind: "separator", id: "s1" },
    // Mockup v3 P1: the four tabs of the own account.
    ...ACCOUNT_TABS.map(
      (tab): MenuEntry => ({
        kind: "item",
        id: `account-${tab.segment}`,
        label: t(tab.labelKey),
        icon: tab.icon,
        onSelect: () => {
          void navigate(accountHref(tab));
        },
      }),
    ),
    ...extra,
    { kind: "separator", id: "s2" },
    {
      kind: "item",
      id: "sign-out",
      label: t("app.nav.signOut"),
      icon: "logout",
      danger: true,
      onSelect: () => {
        handleSignOut();
      },
    },
  ];

  return (
    <Menu
      label={t("app.shell.account")}
      entries={entries}
      renderTrigger={(trigger) => (
        <button
          type="button"
          {...trigger}
          className={[styles.control, styles.user].join(" ")}
          aria-label={t("app.user.menu", { name: user.full_name, email: user.email })}
        >
          <Avatar name={user.full_name} imageUrl={me?.avatar_url ?? null} />
          <span className={styles.text}>
            <b>{user.full_name}</b> <small>{user.email}</small>
          </span>
          <span className={styles.chevron}>
            <Icon name="chevron-up-down" size="sm" />
          </span>
        </button>
      )}
    />
  );
}
